import { useState, useEffect } from 'react';
import { Cloud, HardDrive, RefreshCw, ChevronDown, ChevronRight, Image, Film, FileText, Check, AlertTriangle, Loader2, Database, Download, Upload, Clock, Shield, Trash2, Eye, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const FOLDER_LABELS = {
  'profile_pictures': { label: 'Profile Pictures', icon: 'user' },
  'review_images': { label: 'Review Images', icon: 'image' },
  'report_images': { label: 'Dive Report Images', icon: 'image' },
  'forum_images': { label: 'Forum Images', icon: 'image' },
  'chat_images': { label: 'Chat Images', icon: 'image' },
  'marine_species': { label: 'Marine Species', icon: 'image' },
  'theme_media': { label: 'Theme Media', icon: 'film' },
  'preset_media/images': { label: 'Preset Images', icon: 'image' },
  'preset_media/videos': { label: 'Preset Videos', icon: 'film' },
  'backups': { label: 'DB Backups', icon: 'file' },
};

const formatSize = (mb) => {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = mb * 1024;
  if (kb >= 1) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  return `${Math.round(kb * 1024)} B`;
};

// Cloud folder name → /api/uploads/{type} serving key
const FOLDER_TO_SERVE_PATH = {
  'profile_pictures': 'profile',
  'review_images': 'review',
  'report_images': 'report',
  'forum_images': 'forum',
  'chat_images': 'chat',
  'marine_species': 'marine_species',
  'theme_media': 'theme',
  'cvs': 'cvs',
  'preset_media/images': 'preset/images',
  'preset_media/videos': 'preset/videos',
};
const fileUrl = (folder, filename) => `${API}/uploads/${FOLDER_TO_SERVE_PATH[folder] || folder}/${filename}`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const isImageFile = (filename) => /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
const isVideoFile = (filename) => /\.(mp4|mov|webm|avi)$/i.test(filename);
const isJsonFile = (filename) => /\.json$/i.test(filename);

// ===== File Preview Modal =====
const FilePreviewModal = ({ file, folder, onClose, onDelete }) => {
  const isImg = isImageFile(file.filename);
  const isVid = isVideoFile(file.filename);
  const isJson = isJsonFile(file.filename);
  const [deleting, setDeleting] = useState(false);

  const previewUrl = isImg || isVid ? fileUrl(folder, file.filename) : null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[10001] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-800 truncate">{file.filename}</p>
            {file.context && <p className="text-xs text-cyan-600">{file.context}</p>}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg ml-2">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {isImg && previewUrl && (
            <img src={previewUrl} alt={file.filename} className="w-full rounded-lg" onError={e => { e.target.src = ''; e.target.alt = 'Failed to load'; }} />
          )}
          {isVid && previewUrl && (
            <video src={previewUrl} controls className="w-full rounded-lg" />
          )}
          {isJson && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500">JSON backup file</p>
              <p className="text-xs text-gray-400 mt-1">{file.filename}</p>
            </div>
          )}
          {!isImg && !isVid && !isJson && (
            <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-400 text-sm">
              No preview available for this file type
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-500">
            <div><span className="text-gray-400">Size:</span> {formatSize(file.size / (1024 * 1024))}</div>
            <div><span className="text-gray-400">Modified:</span> {formatDate(file.last_modified)}</div>
            <div className="col-span-2"><span className="text-gray-400">Path:</span> <span className="font-mono text-[10px]">{file.path}</span></div>
          </div>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <Button
            onClick={async () => {
              setDeleting(true);
              await onDelete(file);
              setDeleting(false);
              onClose();
            }}
            disabled={deleting}
            variant="outline"
            size="sm"
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid="cloud-file-delete-btn"
          >
            {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
            Delete from Cloud
          </Button>
          {(isImg || isVid) && previewUrl && (
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-600 hover:underline">
              Open in new tab
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== Folder Row with context + view + delete =====
const FolderRow = ({ folder, info, onDeleteFile, onViewFile, onDeleteUnreferenced }) => {
  const [expanded, setExpanded] = useState(false);
  const meta = FOLDER_LABELS[folder] || { label: folder, icon: 'file' };
  const synced = info.cloud_count >= info.local_count && info.cloud_count > 0;
  const canBulkClean = folder !== 'backups' && info.unreferenced_count > 0;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`cloud-folder-${folder.replace(/\//g, '-')}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          {meta.icon === 'film' ? <Film className="w-4 h-4 text-purple-500" /> :
           meta.icon === 'user' ? <Image className="w-4 h-4 text-blue-500" /> :
           meta.icon === 'file' ? <FileText className="w-4 h-4 text-amber-500" /> :
           <Image className="w-4 h-4 text-cyan-500" />}
          <span className="font-medium text-gray-800 text-sm">{meta.label}</span>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1 text-gray-500">
            <Cloud className="w-3.5 h-3.5" /> {info.cloud_count}
          </span>
          {info.local_count > 0 && (
            <span className="flex items-center gap-1 text-gray-500">
              <HardDrive className="w-3.5 h-3.5" /> {info.local_count}
            </span>
          )}
          <span className="text-gray-400 text-xs">{formatSize(info.cloud_size_mb)}</span>
          {synced ? <Check className="w-4 h-4 text-green-500" /> : 
           info.cloud_count === 0 && info.local_count === 0 ? <span className="text-gray-300 text-xs">empty</span> : 
           <AlertTriangle className="w-4 h-4 text-amber-500" />}
        </div>
      </button>
      {expanded && info.files?.length > 0 && (
        <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-3">
          {canBulkClean && (
            <div className="flex items-center justify-between gap-3 mb-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-xs text-amber-800">
                <strong>{info.unreferenced_count}</strong> file{info.unreferenced_count !== 1 ? 's' : ''} not referenced anywhere in <em>this environment's</em> database (old tests, duplicates, removed pics). Run cleanups from the live site.
                {folder.startsWith('preset_media') && ' Preset media only appears in the theme picker — deleting it removes that option.'}
              </p>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 flex-shrink-0" onClick={() => onDeleteUnreferenced(folder, info.unreferenced_count)} data-testid={`cloud-clean-${folder.replace(/\//g, '-')}`}>
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete unreferenced
              </Button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {info.files.map((file) => (
              <div
                key={file.path}
                className={`flex items-center gap-2 bg-white rounded-lg px-3 py-2 border hover:border-cyan-200 hover:bg-cyan-50/30 cursor-pointer transition-colors group ${file.referenced === false ? 'border-amber-200' : 'border-gray-100'}`}
                onClick={() => onViewFile(file, folder)}
                data-testid={`cloud-file-${file.filename}`}
              >
                {isImageFile(file.filename) ? (
                  <img
                    src={fileUrl(folder, file.filename)}
                    alt="" className="w-10 h-10 rounded object-cover flex-shrink-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : isVideoFile(file.filename) ? (
                  <Film className="w-10 h-10 text-purple-300 flex-shrink-0 p-1" />
                ) : isJsonFile(file.filename) ? (
                  <FileText className="w-10 h-10 text-amber-300 flex-shrink-0 p-1" />
                ) : (
                  <FileText className="w-10 h-10 text-gray-300 flex-shrink-0 p-1" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-mono text-gray-600 truncate">{file.filename}</p>
                  {file.context && (
                    <p className="text-[10px] text-cyan-600 truncate">{file.context}</p>
                  )}
                  <p className="text-[10px] text-gray-400">
                    {formatSize(file.size / (1024 * 1024))}
                    {file.referenced === false && <span className="ml-1 text-amber-600 font-medium">· unused</span>}
                  </p>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button
                    className="p-1 hover:bg-cyan-100 rounded"
                    onClick={(e) => { e.stopPropagation(); onViewFile(file, folder); }}
                    title="View file"
                  >
                    <Eye className="w-3.5 h-3.5 text-cyan-600" />
                  </button>
                  <button
                    className="p-1 hover:bg-red-100 rounded"
                    onClick={(e) => { e.stopPropagation(); onDeleteFile(file); }}
                    title="Delete file"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {expanded && (!info.files || info.files.length === 0) && (
        <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-6 text-center text-sm text-gray-400">No files</div>
      )}
    </div>
  );
};

// ===== Main Component =====
const CloudStorageManager = ({ token }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [cloudData, setCloudData] = useState(null);
  const [dbStatus, setDbStatus] = useState(null);
  const [backups, setBackups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [message, setMessage] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewFolder, setPreviewFolder] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cloudResp, dbResp, backupsResp] = await Promise.all([
        axios.get(`${API}/admin/cloud-storage-files`, { headers }),
        axios.get(`${API}/admin/db-status`, { headers }),
        axios.get(`${API}/admin/cloud-backups`, { headers }),
      ]);
      setCloudData(cloudResp.data);
      setDbStatus(dbResp.data);
      setBackups(backupsResp.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [token]);

  const handleDeleteFile = async (file) => {
    if (!window.confirm(`Delete "${file.filename}" from cloud storage? Any profile/species/chat references to it will be removed too. This cannot be undone.`)) return;
    try {
      const resp = await axios.delete(`${API}/admin/cloud-file?cloud_path=${encodeURIComponent(file.path)}`, { headers });
      const refs = resp.data.references_removed || {};
      const refNote = Object.keys(refs).length ? ` (cleared refs: ${Object.entries(refs).map(([k, v]) => `${k} ×${v}`).join(', ')})` : '';
      toast.success(`Deleted ${file.filename}${refNote}`);
      fetchAll();
    } catch (err) {
      toast.error(`Delete failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleClearBroken = async () => {
    if (!window.confirm("Remove all broken file links from the database? Affected members will see a default avatar until they re-upload.")) return;
    try {
      const resp = await axios.post(`${API}/admin/cloud-files/clear-broken-references`, {}, { headers });
      toast.success(resp.data.message);
      fetchAll();
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleRelocateSpecies = async () => {
    try {
      const resp = await axios.post(`${API}/admin/marine/relocate-images`, {}, { headers });
      toast.success(resp.data.message);
      fetchAll();
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleDeleteUnreferenced = async (folder, count) => {
    if (!window.confirm(`Delete ${count} unreferenced file${count !== 1 ? 's' : ''} from "${folder}"? Only files nothing in the database points to will be removed. This cannot be undone.`)) return;
    try {
      const resp = await axios.post(`${API}/admin/cloud-files/delete-unreferenced`, { folder }, { headers });
      toast.success(resp.data.message);
      fetchAll();
    } catch (err) {
      toast.error(`Cleanup failed: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleViewFile = (file, folder) => {
    setPreviewFile(file);
    setPreviewFolder(folder);
  };

  const handleMigrate = async () => {
    setMigrating(true);
    setMessage(null);
    try {
      const resp = await axios.post(`${API}/admin/migrate-uploads-to-cloud`, {}, { headers });
      setMessage({ type: 'success', text: resp.data.message });
      fetchAll();
    } catch (err) {
      setMessage({ type: 'error', text: `Sync failed: ${err.response?.data?.detail || err.message}` });
    } finally {
      setMigrating(false);
    }
  };

  const handleBackupNow = async () => {
    setBackingUp(true);
    setMessage(null);
    try {
      const resp = await axios.post(`${API}/admin/backup-now`, {}, { headers });
      setMessage({ type: 'success', text: `${resp.data.message} (${resp.data.size_mb} MB)` });
      fetchAll();
    } catch (err) {
      setMessage({ type: 'error', text: `Backup failed: ${err.response?.data?.detail || err.message}` });
    } finally {
      setBackingUp(false);
    }
  };

  const handleRestore = async (cloudPath) => {
    if (!window.confirm('This will import data from this backup. Existing records with the same IDs will be updated. Continue?')) return;
    setRestoring(cloudPath);
    setMessage(null);
    try {
      const resp = await axios.post(`${API}/admin/restore-from-cloud`, { cloud_path: cloudPath }, { headers });
      setMessage({ type: 'success', text: `Restore complete (db version: ${resp.data.db_version})` });
      fetchAll();
    } catch (err) {
      setMessage({ type: 'error', text: `Restore failed: ${err.response?.data?.detail || err.message}` });
    } finally {
      setRestoring(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: 'files', label: 'Cloud Files', icon: Cloud },
    { id: 'backups', label: 'DB Backups', icon: Database },
  ];

  return (
    <div className="space-y-6" data-testid="cloud-storage-manager">
      {/* File Preview Modal */}
      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          folder={previewFolder}
          onClose={() => { setPreviewFile(null); setPreviewFolder(null); }}
          onDelete={handleDeleteFile}
        />
      )}

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Status message */}
      {message && (
        <div className={`px-4 py-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}

      {/* ===== OVERVIEW TAB ===== */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><Cloud className="w-3.5 h-3.5" /> Cloud Files</div>
              <p className="text-2xl font-bold text-gray-800">{cloudData?.summary?.total_cloud_files || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><Database className="w-3.5 h-3.5" /> DB Documents</div>
              <p className="text-2xl font-bold text-gray-800">{dbStatus?.total_documents || 0}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><HardDrive className="w-3.5 h-3.5" /> Cloud Size</div>
              <p className="text-2xl font-bold text-gray-800">{formatSize(cloudData?.summary?.total_cloud_size_mb || 0)}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><Clock className="w-3.5 h-3.5" /> Backups</div>
              <p className="text-2xl font-bold text-gray-800">{backups?.count || 0}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={fetchAll} variant="outline" size="sm" data-testid="cloud-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button onClick={handleMigrate} disabled={migrating} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="cloud-migrate-btn">
              {migrating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              {migrating ? 'Syncing...' : 'Sync Files to Cloud'}
            </Button>
            <Button onClick={handleBackupNow} disabled={backingUp} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cloud-backup-btn">
              {backingUp ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Database className="w-4 h-4 mr-1" />}
              {backingUp ? 'Backing up...' : 'Backup DB Now'}
            </Button>
          </div>

          {/* DB Collections */}
          {dbStatus && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Database Collections</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {Object.entries(dbStatus.collections).filter(([, v]) => v > 0).map(([name, count]) => (
                  <div key={name} className="bg-white rounded-lg border border-gray-200 px-3 py-2 flex items-center justify-between">
                    <span className="text-xs text-gray-600 truncate">{name}</span>
                    <span className="text-sm font-semibold text-gray-800 ml-2">{count}</span>
                  </div>
                ))}
              </div>
              {dbStatus.last_import && (
                <p className="text-xs text-gray-400 mt-2">
                  Last import: {formatDate(dbStatus.last_import.last_import_date)} (db v{dbStatus.last_import.db_version}, from {dbStatus.last_import.filename || '?'})
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== CLOUD FILES TAB ===== */}
      {activeTab === 'files' && cloudData && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Storage Folders</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">{cloudData.summary.total_cloud_files} files, {formatSize(cloudData.summary.total_cloud_size_mb)}</span>
              <Button size="sm" variant="outline" onClick={handleRelocateSpecies} data-testid="relocate-species-images-btn" title="Move species photos uploaded via other folders into Marine Species">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Relocate species images
              </Button>
            </div>
          </div>
          {cloudData.broken_references?.length > 0 && (
            <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4 mb-3" data-testid="broken-references-panel">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-red-800">{cloudData.broken_references.length} broken link{cloudData.broken_references.length !== 1 ? 's' : ''}</p>
                  <p className="text-xs text-red-700 mt-0.5">These database records point to files that no longer exist in the cloud or on the server (lost before cloud sync was fixed). Members will need to re-upload.</p>
                </div>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-100 bg-white flex-shrink-0" onClick={handleClearBroken} data-testid="clear-broken-refs-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear broken links
                </Button>
              </div>
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-red-900">
                {cloudData.broken_references.slice(0, 40).map((r) => (
                  <li key={r.filename} className="truncate"><span className="font-medium">{r.label || r.collection}</span> <span className="text-red-500">({r.collection})</span> — {r.filename}</li>
                ))}
                {cloudData.broken_references.length > 40 && <li>…and {cloudData.broken_references.length - 40} more</li>}
              </ul>
            </div>
          )}
          {Object.entries(cloudData.folders)
            .filter(([folder]) => folder !== 'backups')
            .map(([folder, info]) => (
              <FolderRow key={folder} folder={folder} info={info} onDeleteFile={handleDeleteFile} onViewFile={handleViewFile} onDeleteUnreferenced={handleDeleteUnreferenced} />
            ))}
          {cloudData.folders.backups && (
            <FolderRow folder="backups" info={cloudData.folders.backups} onDeleteFile={handleDeleteFile} onViewFile={handleViewFile} onDeleteUnreferenced={handleDeleteUnreferenced} />
          )}
        </div>
      )}

      {/* ===== DB BACKUPS TAB ===== */}
      {activeTab === 'backups' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Cloud Database Backups</h3>
            <Button onClick={handleBackupNow} disabled={backingUp} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {backingUp ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Database className="w-4 h-4 mr-1" />}
              {backingUp ? 'Creating...' : 'Create Backup'}
            </Button>
          </div>

          {backups?.backups?.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">No cloud backups found. Click "Create Backup" to create one.</div>
          )}

          <div className="space-y-2">
            {backups?.backups?.map((backup) => (
              <div key={backup.path} className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate">{backup.filename}</p>
                  <p className="text-xs text-gray-400">{formatDate(backup.last_modified)} &middot; {formatSize(backup.size_mb)}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleRestore(backup.path)}
                    disabled={restoring === backup.path}
                    variant="outline"
                    size="sm"
                    data-testid={`restore-btn-${backup.filename}`}
                  >
                    {restoring === backup.path ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                    Restore
                  </Button>
                  <Button
                    onClick={() => handleDeleteFile({ path: backup.path, filename: backup.filename })}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`delete-backup-btn-${backup.filename}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
            <strong>Auto-restore:</strong> If the database is empty on startup, the server will automatically restore from the latest cloud backup.
          </div>
        </div>
      )}
    </div>
  );
};

export default CloudStorageManager;
